import { useEffect, useState } from "react";

import { RETENTION_PERIOD_PRESETS } from "./constants";

import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Permissions } from "@/providers/RbacProvider/constants";
import { useRbac } from "@/providers/RbacProvider";
import { useUpdateWorkspace } from "@/queries/workspace";
import { DataRetentionPeriod } from "@/types/workspaces";
import { displayToast } from "@/utils/toast";

const VALID_RETENTION_PERIODS = RETENTION_PERIOD_PRESETS.map(preset => preset.value);

const isValidRetentionPeriod = (value: unknown): value is DataRetentionPeriod =>
  typeof value === "string" && VALID_RETENTION_PERIODS.includes(value as DataRetentionPeriod);

const getRetentionPeriodLabel = (value: DataRetentionPeriod | undefined) =>
  RETENTION_PERIOD_PRESETS.find(preset => preset.value === value)?.label ?? "";

export const RetentionPolicy = () => {
  const { activeWorkspace } = useUser();
  const { permissions } = useRbac();
  const isAdmin = permissions?.includes(Permissions.ADMIN) ?? false;

  const workspaceId = activeWorkspace?.id;
  const rawSavedPeriod = activeWorkspace?.data_retention_period;
  const savedPeriod = isValidRetentionPeriod(rawSavedPeriod) ? rawSavedPeriod : undefined;

  const [selectedPeriod, setSelectedPeriod] = useState<DataRetentionPeriod | undefined>(undefined);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { mutate: updateWorkspace, isPending } = useUpdateWorkspace();

  useEffect(() => {
    if (workspaceId === undefined) {
      return;
    }

    if (isValidRetentionPeriod(rawSavedPeriod)) {
      setSelectedPeriod(rawSavedPeriod);
    } else {
      displayToast(
        "Couldn't load this workspace's retention period — it's missing or unrecognized. Please select one below.",
        ToastType.error,
      );
    }
  }, [workspaceId, rawSavedPeriod]);

  const hasChanges = selectedPeriod !== undefined && selectedPeriod !== savedPeriod;

  const handleSubmit = () => {
    if (!hasChanges || !isAdmin) {
      return;
    }
    setIsConfirmOpen(true);
  };

  const confirmChange = () => {
    if (workspaceId === undefined || selectedPeriod === undefined) {
      return;
    }
    updateWorkspace(
      { workspaceId, data_retention_period: selectedPeriod },
      { onSuccess: () => setIsConfirmOpen(false) },
    );
  };

  const cancelChange = () => {
    setSelectedPeriod(savedPeriod);
    setIsConfirmOpen(false);
  };

  const currentLabel = getRetentionPeriodLabel(savedPeriod) || "Not set";
  const newLabel = getRetentionPeriodLabel(selectedPeriod);

  return (
    <div className="flex max-w-xl flex-col gap-6 pb-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-900">Data retention period</h2>
        <p className="text-sm text-gray-500">
          Choose how long request data is retained for this workspace. Older data is removed once it
          exceeds the selected period.
        </p>
        {!isAdmin ? (
          <p className="text-sm text-gray-500">
            Only workspace admins can change the retention policy.
          </p>
        ) : null}
      </div>

      <Select
        value={selectedPeriod}
        onValueChange={value => setSelectedPeriod(value as DataRetentionPeriod)}
        disabled={!isAdmin}
      >
        <SelectTrigger className="max-w-xs">
          <SelectValue placeholder="Select a retention period" />
        </SelectTrigger>
        <SelectContent>
          {RETENTION_PERIOD_PRESETS.map(preset => (
            <SelectItem key={preset.id} value={preset.value}>
              {preset.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div>
        <Button onClick={handleSubmit} disabled={!hasChanges || !isAdmin}>
          Save retention policy
        </Button>
      </div>

      <ConfirmationModal
        title="Confirm retention period change"
        description="Reducing the retention period may permanently delete request data older than the new period. This action cannot be undone."
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        leaveButtonText="Cancel"
        saveButtonText="Confirm Change"
        headerVariant={ConfirmationModalVariant.warning}
        saveButtonVariant="default"
        exitButtonVariant="outline"
        useBlocker={false}
        isLoading={isPending}
        handleOnConfirm={confirmChange}
        handleLeave={cancelChange}
        handleClose={cancelChange}
      >
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
            <span className="text-gray-500">Current</span>
            <span className="font-medium text-gray-900">{currentLabel}</span>
          </div>
          <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
            <span className="text-gray-500">New</span>
            <span className="font-medium text-gray-900">{newLabel}</span>
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default RetentionPolicy;
